import { useAccountEquity } from '../../../hooks/useAccountEquity';
import { useDryRun } from '../../../hooks/useDryRunWs';
import { useMarketOrderPriceImpact } from '../../../hooks/useMarketOrderPriceImpact';
import { useModalState } from '../../../hooks/useModalState';
import { usePositions } from '../../../hooks/usePositions';
import { useProductPricesSub } from '../../../hooks/useProductPricesSub';
import { ProductType } from '../../../hooks/useProducts';
import { useActiveSelectedSubaccount } from '../../../hooks/useSelectedSubaccount';
import {
  formatDollar,
  formatLargeDollar,
  formatLargeNumber,
  formatPercentage,
  formatPercentWithDecimals,
} from '../../../lib/formatters';
import { Gwei } from '../../../lib/gwei';
import { multiply } from '../../../lib/math';
import { tifOptions } from '../../../lib/tifOptions';
import {
  feesTooltip,
  liquidationPriceTooltip,
  orderValueTooltip,
  slippageTooltip,
} from '../../../lib/tooltips';
import { OrderType, TimeInForce } from '../../../types/orders';
import { Button } from '../../catalyst-ui-kit/button';
import { Tooltip } from '../../Tooltip/Tooltip';
import {
  useCurrentPosition,
  useNewPositionSize,
  usePriceImpact,
  useProjectedLiquidationPrice,
} from './hooks/calculationHooks';
import { useTradeFormStore } from './TradeFormState';
import { StatRow, StatRowValue } from './TradeFormStats';

interface TradeFormInfoUiProps {
  product: ProductType;
  marketPriceImpact: bigint | undefined;
  requiredMarginForPos: bigint | undefined;
  limitPriceImpact: bigint | undefined;
  executionPrice: bigint | undefined;
  liqPriceAfter: bigint | undefined;
  orderType: OrderType;
  tif: TimeInForce;
  expiryInDays: number;
  orderValue: bigint | undefined;
}

export const TradeFormInfoUi: React.FC<TradeFormInfoUiProps> = ({
  product,
  executionPrice,
  requiredMarginForPos,
  limitPriceImpact,
  marketPriceImpact,
  orderType,
  liqPriceAfter,
  tif,
  expiryInDays,
  orderValue,
}) => {
  const setTifModal = useModalState((state) => state.setTifModal);

  const priceImpact = orderType === 'LIMIT' ? limitPriceImpact : marketPriceImpact;

  return (
    <div className="flex flex-col gap-2 max-lg:mt-3">
      <StatRow label={<Tooltip content={liquidationPriceTooltip()}>Liquidation Price</Tooltip>}>
        <StatRowValue value={liqPriceAfter} formatValue={(x) => `~${formatLargeNumber(x)}`} />
      </StatRow>
      <StatRow
        label={
          orderType === 'MARKET' && executionPrice ? (
            <Tooltip content={orderValueTooltip(executionPrice)}>Order Value</Tooltip>
          ) : (
            'Order Value'
          )
        }
      >
        <StatRowValue
          value={orderValue}
          formatValue={(val) => `${orderType === 'LIMIT' ? '' : '~'}${formatDollar(val)}`}
        />
      </StatRow>
      <StatRow label="Margin Required">
        <StatRowValue value={requiredMarginForPos} formatValue={formatLargeDollar} />
      </StatRow>

      {orderType === 'MARKET' && (
        <StatRow
          label={<Tooltip content={slippageTooltip()}>Slippage</Tooltip>}
          childrenWrapperClassName="gap-1"
        >
          <StatRowValue
            value={orderType === 'MARKET' ? priceImpact : undefined}
            formatValue={formatPercentage}
            reverseColor
            colorised={(priceImpact && priceImpact > Gwei.toGwei(0.05)) || false}
          />{' '}
          <span>|</span> Max: 5%
        </StatRow>
      )}

      <StatRow label={<Tooltip content={feesTooltip()}>Fees</Tooltip>}>
        {formatPercentWithDecimals(product.takerFee)} /{' '}
        {formatPercentWithDecimals(product.makerFee)}
      </StatRow>
      {tif && orderType === 'LIMIT' ? (
        <StatRow
          label={
            <Tooltip content={tifOptions[tif].tooltip}>
              {tif === 'GTD' ? tifOptions[tif].dropdownLabel : 'Time In Force'}
            </Tooltip>
          }
        >
          <Button size="2xs" text onClick={() => setTifModal('open')}>
            {tif === 'GTD' ? <>{expiryInDays} days</> : tifOptions[tif].dropdownLabel}
          </Button>
        </StatRow>
      ) : null}
    </div>
  );
};

/**
 * Hook to compose all the calculations needed for TradeFormInfo
 */
function useTradeFormInfoData(product: ProductType) {
  const selectedSubaccount = useActiveSelectedSubaccount();
  const { data: positions } = usePositions({ subaccountId: selectedSubaccount?.id });
  const { result } = useDryRun();

  const { size: currentPositionSize } = useCurrentPosition(positions, product.id);

  const orderType = useTradeFormStore(({ orderType }) => orderType);
  const limitPrice = useTradeFormStore(({ limitPrice }) => limitPrice);
  const side = useTradeFormStore(({ side }) => side);
  const amount = useTradeFormStore(({ amount }) => amount);
  const midPrice = useTradeFormStore(({ midPrice }) => midPrice);
  const postOnly = useTradeFormStore(({ postOnly }) => postOnly);
  const tif = useTradeFormStore(({ tif }) => tif);
  const expiryInDays = useTradeFormStore(({ expiryInDays }) => expiryInDays);

  const { priceImpact: marketPriceImpact, executionPrice } = useMarketOrderPriceImpact(
    product.id,
    side,
    amount || 0n
  );

  const limitPriceImpact = usePriceImpact(limitPrice, midPrice, side);

  const price = orderType === 'LIMIT' ? limitPrice : executionPrice;
  const orderValue = multiply(amount || 0n, price || 0n);
  const newPosSize = useNewPositionSize(currentPositionSize, amount, side);

  const { data: pricesByProductId } = useProductPricesSub();
  const { oraclePrice } = pricesByProductId[product.id] || {};

  const accountEquity = useAccountEquity();
  const liqPriceAfter = useProjectedLiquidationPrice({
    accountEquity,
    orderSize: amount,
    newPositionSize: newPosSize,
    oraclePrice: oraclePrice,
    executionPrice: price,
    feeRate: postOnly ? product.makerFee : product.takerFee,
    maintenanceMarginRatio: product.maintenanceMarginRatio,
    currentPositionSize,
  });

  return {
    marketPriceImpact,
    requiredMarginForPos: result?.marginRequired,
    limitPriceImpact,
    executionPrice: price,
    liqPriceAfter,
    orderType,
    tif,
    expiryInDays,
    orderValue,
  };
}

export const TradeFormInfo: React.FC<{ product: ProductType }> = ({ product }) => {
  const infoData = useTradeFormInfoData(product);

  return (
    <TradeFormInfoUi
      product={product}
      marketPriceImpact={infoData.marketPriceImpact}
      requiredMarginForPos={infoData.requiredMarginForPos}
      limitPriceImpact={infoData.limitPriceImpact}
      executionPrice={infoData.executionPrice}
      liqPriceAfter={infoData.liqPriceAfter}
      orderType={infoData.orderType}
      tif={infoData.tif}
      expiryInDays={infoData.expiryInDays}
      orderValue={infoData.orderValue}
    />
  );
};
